"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useActiveCommandAgent } from "@/hooks/use-active-command-agent";
import { useCommandPanelThread } from "@/hooks/use-command-panel-thread";
import { isCommandPanelDisabledRoute } from "@/utils/command-panel-routes";

type CommandPanelThread = ReturnType<typeof useCommandPanelThread>;

interface CommandPanelContextValue {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    activeAgent: ReturnType<typeof useActiveCommandAgent>["activeAgent"];
    isLoadingActiveAgent: boolean;
    canListAgents: boolean;
    thread: CommandPanelThread;
}

const CommandPanelContext = createContext<CommandPanelContextValue | null>(null);

export function useCommandPanel(): CommandPanelContextValue {
    const ctx = useContext(CommandPanelContext);
    if (!ctx) throw new Error("useCommandPanel must be used within a CommandPanelProvider");
    return ctx;
}

export function CommandPanelProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const pathname = usePathname();
    const { activeAgent, isLoadingActiveAgent, canListAgents } = useActiveCommandAgent();
    const thread = useCommandPanelThread(activeAgent?.id ?? null);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

    // Auth pages have no authenticated session to back the panel, and
    // super-admin is a separate admin area — force it closed the moment
    // either is reached (e.g. logging out while it was left open).
    useEffect(() => {
        if (isCommandPanelDisabledRoute(pathname)) setIsOpen(false);
    }, [pathname]);

    // Every layout's content wrapper shifts over via the shared
    // --command-panel-width variable (see .command-panel-gutter in
    // globals.css) so pushing the page over doesn't require the panel
    // itself to live inside each layout's own flex tree.
    useEffect(() => {
        document.documentElement.setAttribute("data-command-panel-open", String(isOpen));
        return () => {
            document.documentElement.removeAttribute("data-command-panel-open");
        };
    }, [isOpen]);

    const value = useMemo<CommandPanelContextValue>(
        () => ({ isOpen, open, close, toggle, activeAgent, isLoadingActiveAgent, canListAgents, thread }),
        [isOpen, open, close, toggle, activeAgent, isLoadingActiveAgent, canListAgents, thread]
    );

    return <CommandPanelContext.Provider value={value}>{children}</CommandPanelContext.Provider>;
}
